import React, { useEffect } from 'react';
import { Typography, Button, message, Space } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { useTermsAndConditionsStore } from '../stores/termsAndConditionsStore';
import { TermsAndConditionsFilterComponent } from '../components/TermsAndConditionsFilter';
import { TermsAndConditionsTable } from '../components/TermsAndConditionsTable';
import { TermsAndConditionsDTO, TermsAndConditionsFilter } from '../types';

const { Title } = Typography;

export const ListView: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentItem } = useTermsAndConditionsStore();
  
  const {
    termsAndConditions,
    loading,
    filter,
    setFilter,
    refetch,
    deleteTermsAndConditions,
    activateTermsAndConditions,
    deactivateTermsAndConditions
  } = useTermsAndConditions();

  // Reset current item when navigating to list view
  useEffect(() => {
    setCurrentItem(null);
  }, [setCurrentItem]);

  const handleFilter = (newFilter: TermsAndConditionsFilter) => {
    setFilter(newFilter);
  };

  const handleView = (record: any) => {
    setCurrentItem(record);
    navigate(`/terms-and-conditions/${record.id}`);
  };

  const handleEdit = (record: any) => {
    setCurrentItem(record);
    navigate(`/terms-and-conditions/${record.id}/edit`);
  };

  const handleDelete = async (id: string) => {
    const success = await deleteTermsAndConditions(id);
    if (success) {
      message.success('Terms and conditions deleted successfully');
      refetch();
    }
  };

  const handleActivate = async (id: string) => {
    const success = await activateTermsAndConditions(id);
    if (success) {
      message.success('Terms and conditions activated successfully');
      refetch();
    }
  };

  const handleDeactivate = async (id: string) => {
    const success = await deactivateTermsAndConditions(id);
    if (success) {
      message.success('Terms and conditions deactivated successfully');
      refetch();
    }
  };

  const handleCreate = () => {
    navigate('/terms-and-conditions/create');
  };

  // Apply filters to the data
  const filteredData = termsAndConditions.filter((item: TermsAndConditionsDTO) => {
    if (filter.type && item.type !== filter.type) {
      return false;
    }
    if (filter.entityId && item.entityId !== filter.entityId) {
      return false;
    }
    if (filter.showActive && !item.active) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <Title level={3}>Terms and Conditions</Title>
        <Space>
          <Button 
            onClick={() => refetch()} 
            icon={<ReloadOutlined />}
            loading={loading}
          >
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create New
          </Button>
        </Space>
      </div>

      <TermsAndConditionsFilterComponent 
        onFilter={handleFilter}
        loading={loading}
      />

      <TermsAndConditionsTable 
        data={filteredData}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
      />
    </div>
  );
};
